import React, { useEffect, useMemo, useState } from 'react';
import ChampHoraires from './ChampHoraires';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Loader2, MapPin, Search, UserPlus, CheckCircle2, Circle, Sparkles, Users, X } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { nsfLabel } from '../constants/nsf';
import { Field, inputCls, textareaCls } from './session/SessionFormShared';
import SessionDateRangesEditor from './session/SessionDateRangesEditor';

const MODES = ['Présentiel', 'Distanciel', 'Mixte'];
const STATUSES = ['Préinscription', 'Ouverte', 'Confirmée', 'Complète', 'En cours', 'Terminée'];

const EMPTY_LEARNER = { firstName: '', lastName: '', email: '', phone: '', company: '' };

const EMPTY_SESSION = {
  title: '', program: '', programme_id: '', trainer: '', startDate: '', endDate: '',
  duration: 0, capacity: 10, mode: 'Présentiel', status: 'Ouverte', location: '',
  description: '', objectives: '', prerequisites: '', public_cible: '', price: 0, programme_pdf_url: '', nsf_code: '',
  horaires: [],
};

/**
 * Rich session create/edit dialog with:
 *  - Programme dropdown (filtered by category) that auto-fills all session fields
 *  - Inline apprenant creation (quick form)
 *  - Multi-select of existing apprenants with pre-check support (from Devis conversion)
 */
export default function SessionFormDialog({
  open,
  onOpenChange,
  mode, // 'create' | 'edit'
  initialValues, // existing session (edit) OR pre-fill (from devis)
  preSelectedLearnerIds, // list of learner_ids to pre-check at open
  onCreated, // (session) => void
  onUpdated, // (session) => void
  preinscription = false, // when true, the dialog is framed as a "pré-inscription"
  lockedFromQuote = null, // when set ({ quoteNumber }), programme & price fields are read-only to stay consistent with the signed quote
  existingSessionId = null, // when set, finalize just enrolls learners into this session (no duplicate creation)
  companyScope = null, // { id, name } — restrict learner pool to this company (used in pré-inscription flow)
}) {
  const isEdit = mode === 'edit';
  const [values, setValues] = useState(EMPTY_SESSION);
  const [programs, setPrograms] = useState([]);
  // **Les lieux qu'on peut choisir plutot que taper.** L'adresse de la maison
  // existait en sept orthographes (dont un « Louis Amand » sur 17 sessions) et
  // celle d'un client en « LZ PASSAGE » : un champ libre sur une donnee que la
  // base connait est une faute de frappe en attente.
  const [lieux, setLieux] = useState([]);
  const [lieuOuvert, setLieuOuvert] = useState(false);
  const [lieuEnTete, setLieuEnTete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [allLearners, setAllLearners] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState(new Set());
  const [learnerSearch, setLearnerSearch] = useState('');
  const [inlineOpen, setInlineOpen] = useState(false);
  const [newLearner, setNewLearner] = useState(EMPTY_LEARNER);
  const [creatingLearner, setCreatingLearner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Pré-inscription mode picker — when locked from a quote AND no session is
  // pre-linked, the user can either CREATE a new session (with custom dates)
  // OR pick an EXISTING session to enroll the learners into.
  const [preMode, setPreMode] = useState('new'); // 'new' | 'existing'
  const [chosenSessionId, setChosenSessionId] = useState('');
  const [allSessions, setAllSessions] = useState([]);

  // Effective target session id: prop (devis-linked) wins, otherwise local pick
  const effectiveExistingSessionId = existingSessionId || (preMode === 'existing' ? (chosenSessionId || null) : null);

  // When finalizing a pré-inscription into an existing session, we load the
  // live enrollments of that session so the user sees who's already there
  // and never gets confused by the "N déjà inscrits" skip count.
  const [existingEnrollments, setExistingEnrollments] = useState([]);
  useEffect(() => {
    if (!open || !effectiveExistingSessionId) { setExistingEnrollments([]); return; }
    api.get(`/sessions/${effectiveExistingSessionId}/learners`)
      .then((r) => setExistingEnrollments(r.data || []))
      .catch(() => setExistingEnrollments([]));
  }, [open, effectiveExistingSessionId]);

  // Load existing sessions when a quote-locked pre-registration may need to
  // pick an already-existing session.
  useEffect(() => {
    if (!open || !lockedFromQuote || existingSessionId) return;
    api.get('/sessions').then((r) => setAllSessions(r.data || [])).catch(() => setAllSessions([]));
  }, [open, lockedFromQuote, existingSessionId]);

  // L'horaire type de la maison. Il **propose**, il n'impose pas : il se
  // recopie sur une formation neuve et n'est plus relu ensuite — sinon le
  // changer dans les reglages reecrirait l'horaire d'une convention signee.
  const [horaireType, setHoraireType] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get('/orgs/me')
      .then(({ data }) => setHoraireType(Array.isArray(data.horaires_defaut) ? data.horaires_defaut : []))
      .catch(() => setHoraireType([]));
  }, [open]);

  // La presaisie, a la creation seulement et seulement si rien n'est pose :
  // une formation qu'on corrige ne se fait pas reecrire son horaire.
  useEffect(() => {
    if (!open || isEdit || !horaireType.length) return;
    setValues((v) => ((v.horaires || []).length ? v : { ...v, horaires: horaireType.map((c) => ({ ...c })) }));
  }, [open, isEdit, horaireType]);

  // Reset mode picker when dialog re-opens
  useEffect(() => {
    if (!open) return;
    setPreMode('new');
    setChosenSessionId('');
    setLieuOuvert(false);
  }, [open]);

  // Load programmes, categories, and learners on open
  useEffect(() => {
    if (!open) return;
    setError(null);
    Promise.all([
      api.get('/programs').then((r) => r.data).catch(() => []),
      api.get('/categories').then((r) => r.data).catch(() => []),
      api.get('/learners').then((r) => r.data).catch(() => []),
      // Le serveur ordonne : l'entreprise que la session designe en tete,
      // nos locaux, puis les autres. L'ecran ne decide rien.
      api.get('/lieux-formation', {
        params: {
          session_id: isEdit ? (initialValues?.id || undefined) : undefined,
          company_id: companyScope?.id || undefined,
        },
      }).then((r) => r.data || {}).catch(() => ({})),
    ]).then(([progs, cats, lrs, rl]) => {
      setLieux((rl && rl.lieux) || []);
      setLieuEnTete((rl && rl.en_tete) || null);
      setPrograms(progs || []);
      setCategories(cats || []);
      setAllLearners(lrs || []);
    });
  }, [open]);

  // Initialize/reset form when opened
  useEffect(() => {
    if (!open) return;
    const base = isEdit
      ? { ...EMPTY_SESSION, ...(initialValues || {}) }
      : { ...EMPTY_SESSION, ...(initialValues || {}) };
    setValues(base);
    // When finalizing into an existing session, filter out learners that are
    // ALREADY enrolled so the checked-by-default list reflects only who will
    // actually be added. We also filter them out on each render (see below).
    const preSet = new Set(preSelectedLearnerIds || []);
    if (effectiveExistingSessionId && existingEnrollments.length) {
      // Filter out learners already inscribed on this session — but only those
      // that match the company scope (others are not visible anyway).
      existingEnrollments
        .filter((l) => !companyScope?.id || l.company_id === companyScope.id)
        .forEach((l) => preSet.delete(l.id));
    }
    setSelectedLearners(preSet);
    setLearnerSearch('');
    setInlineOpen(false);
    setNewLearner(EMPTY_LEARNER);
    setSelectedCategory('Toutes');
  }, [open, isEdit, initialValues, preSelectedLearnerIds, effectiveExistingSessionId, existingEnrollments, companyScope]);

  // When programmes finish loading, sync the category selector AND backfill any
  // missing session fields from the programme (robust when initialValues came
  // from a devis with only a programme_id + partial fields).
  useEffect(() => {
    if (!open || !values.programme_id || !programs.length) return;
    const p = programs.find((x) => x.id === values.programme_id);
    if (!p) return;
    if (p.category) setSelectedCategory(p.category);
    // Backfill empty fields from programme (non-destructive).
    setValues((s) => ({
      ...s,
      program: s.program || p.title || '',
      title: s.title || p.title || '',
      duration: s.duration || p.duration || 0,
      mode: s.mode || p.mode || 'Présentiel',
      // **Le repère du programme, pas son champ plat.** Cette voie-ci lisait
      // `p.location` — vide sur les quarante-deux fiches depuis que le lieu est
      // un repère — et ignorait donc « dans nos locaux » comme « en
      // entreprise » : une session née d'un devis partait sans lieu. L'autre
      // voie, celle du choix à la main, résolvait correctement. La même règle
      // écrite deux fois, dont une seule était juste.
      location: s.location || lieuDuProgramme(p) || '',
      trainer: s.trainer || p.trainer || '',
      description: s.description || p.description || '',
      objectives: s.objectives || p.objectives || '',
      prerequisites: s.prerequisites || p.prerequisites || '',
      public_cible: s.public_cible || p.public_cible || '',
      price: (s.price ?? 0) > 0 ? s.price : (p.price ?? 0),
      programme_pdf_url: s.programme_pdf_url || p.programme_pdf_url || '',
      nsf_code: s.nsf_code || p.nsf_code || '',
    }));
    // `lieux` est dans les dépendances : les lieux arrivent d'un appel, et sans
    // lui le repère se résoudrait sur une liste encore vide — le lieu resterait
    // alors vide pour toujours. La complétion ne remplit que ce qui manque,
    // donc la repasser quand la liste arrive ne défait rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, programs, values.programme_id, lieux, lieuEnTete]);

  // When the user picks an existing session from the dropdown, auto-fill the
  // form with that session's data so the locked dates / address / etc. reflect
  // the chosen target instead of the (irrelevant) prefill from the quote.
  useEffect(() => {
    if (!open || !chosenSessionId || preMode !== 'existing') return;
    const target = (allSessions || []).find((s) => s.id === chosenSessionId);
    if (!target) return;
    setValues((s) => ({
      ...s,
      title: target.title || s.title,
      program: target.program || s.program,
      programme_id: target.programme_id || s.programme_id,
      trainer: target.trainer || s.trainer,
      mode: target.mode || s.mode,
      startDate: target.startDate || s.startDate,
      endDate: target.endDate || s.endDate,
      duration: target.duration || s.duration,
      capacity: target.capacity || s.capacity,
      location: target.location || s.location,
      status: target.status || s.status,
      price: target.price ?? s.price,
      description: target.description || s.description,
      objectives: target.objectives || s.objectives,
      prerequisites: target.prerequisites || s.prerequisites,
      public_cible: target.public_cible || s.public_cible,
      nsf_code: target.nsf_code || s.nsf_code,
    }));
  }, [chosenSessionId, preMode, allSessions, open]);

  const setVal = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const categoryOptions = useMemo(() => {
    const names = (categories || []).map((c) => c.name).filter(Boolean);
    return ['Toutes', ...names];
  }, [categories]);

  const filteredPrograms = useMemo(() => {
    if (selectedCategory === 'Toutes') return programs;
    return programs.filter((p) => p.category === selectedCategory);
  }, [programs, selectedCategory]);

  // **Le lieu d'un programme est un repere, plus une chaine.** « Dans nos
  // locaux » se resout sur les reglages, « en entreprise » sur le client que
  // le serveur a mis en tete de la liste. Sans repere, le programme garde son
  // adresse a lui — une salle louee.
  const lieuDuProgramme = (p) => {
    const repere = (p && p.lieu_defaut) || '';
    if (repere === 'NOS_LOCAUX') {
      return (lieux.find((l) => l.source === 'nos_locaux') || {}).adresse || '';
    }
    if (repere === 'EN_ENTREPRISE') {
      return (lieux.find((l) => l.company_id && l.company_id === lieuEnTete) || {}).adresse || '';
    }
    return (p && p.location) || '';
  };

  // When user picks a programme, auto-fill session fields
  const handleProgrammeChange = (programmeId) => {
    const p = programs.find((x) => x.id === programmeId);
    if (!p) {
      setVal('programme_id', '');
      return;
    }
    setValues((s) => ({
      ...s,
      programme_id: p.id,
      program: p.title || '',
      title: s.title || p.title || '',
      duration: p.duration || s.duration,
      mode: p.mode || s.mode || 'Présentiel',
      location: lieuDuProgramme(p) || s.location || '',
      trainer: p.trainer || s.trainer || '',
      description: p.description || s.description || '',
      objectives: p.objectives || s.objectives || '',
      prerequisites: p.prerequisites || s.prerequisites || '',
      public_cible: p.public_cible || s.public_cible || '',
      price: p.price ?? s.price ?? 0,
      programme_pdf_url: p.programme_pdf_url || s.programme_pdf_url || '',
      nsf_code: p.nsf_code || s.nsf_code || '',
    }));
    toast({ title: 'Programme chargé', description: `Champs auto-remplis depuis « ${p.title} »` });
  };

  const toggleLearner = (id) => setSelectedLearners((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const filteredLearners = useMemo(() => {
    const q = learnerSearch.trim().toLowerCase();
    // When a company scope is enforced (pré-inscription from devis), only
    // learners belonging to that company are visible. Learners attached to a
    // different company OR with no company at all (Particulier) are excluded
    // for cohérence + RGPD.
    let pool = allLearners;
    if (companyScope?.id) {
      pool = pool.filter((l) => l.company_id === companyScope.id);
    }
    if (!q) return pool;
    return pool.filter((l) =>
      `${l.firstName} ${l.lastName} ${l.email} ${l.company || ''}`.toLowerCase().includes(q)
    );
  }, [allLearners, learnerSearch, companyScope]);

  // Existing enrollments are also scoped — we only show those belonging to
  // the same company so the user doesn't see (or worse, accidentally interact
  // with) participants from another client's session.
  const visibleExistingEnrollments = useMemo(() => {
    if (!companyScope?.id) return existingEnrollments;
    return existingEnrollments.filter((l) => l.company_id === companyScope.id);
  }, [existingEnrollments, companyScope]);

  const handleCreateInlineLearner = async () => {
    if (!newLearner.firstName.trim() || !newLearner.lastName.trim() || !newLearner.email.trim()) {
      toast({ title: 'Informations incomplètes', description: 'Prénom, Nom et Email sont requis.' });
      return;
    }
    setCreatingLearner(true);
    try {
      const payload = {
        firstName: newLearner.firstName.trim(),
        lastName: newLearner.lastName.trim(),
        email: newLearner.email.trim(),
        phone: newLearner.phone.trim(),
        // When the dialog is restricted to a single company, force the new
        // learner to be attached to that company so they appear in the
        // pré-inscription pool right away.
        company: companyScope?.name || newLearner.company.trim(),
        company_id: companyScope?.id || undefined,
        tag: 'Entreprise',
        status: 'Actif',
        progress: 0,
      };
      const { data } = await api.post('/learners', payload);
      setAllLearners((p) => [...p, data]);
      setSelectedLearners((s) => { const n = new Set(s); n.add(data.id); return n; });
      setNewLearner(EMPTY_LEARNER);
      setInlineOpen(false);
      toast({ title: 'Apprenant créé', description: `${data.firstName} ${data.lastName}` });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible de créer le apprenant.' });
    } finally {
      setCreatingLearner(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const learnerIds = Array.from(selectedLearners);
      if (isEdit) {
        // Standard update via register_crud
        const payload = {
          ...values,
          duration: Number(values.duration) || 0,
          capacity: Number(values.capacity) || 0,
          price: Number(values.price) || 0,
        };
        const { data } = await api.put(`/sessions/${initialValues.id}`, payload);
        onUpdated && onUpdated(data);
      } else if (effectiveExistingSessionId) {
        // PRÉ-INSCRIPTION — enroll into the existing session linked on the
        // devis (or chosen interactively) rather than creating a duplicate.
        if (!learnerIds.length) {
          setError("Sélectionnez au moins un apprenant à inscrire à cette session.");
          setLoading(false);
          return;
        }
        const { data } = await api.post(`/sessions/${effectiveExistingSessionId}/enroll-batch`, {
          learner_ids: learnerIds,
        });
        // Reflect to parent with the updated session doc + added count
        onCreated && onCreated(data.session, data.added, { enrolledOnly: true, skipped: data.skipped });
      } else {
        // Create + bulk enroll in one call
        const payload = {
          ...values,
          duration: Number(values.duration) || 0,
          capacity: Math.max(Number(values.capacity) || 0, learnerIds.length),
          price: Number(values.price) || 0,
          learner_ids: learnerIds,
        };
        if (!payload.endDate) payload.endDate = payload.startDate;
        const { data } = await api.post('/sessions/create-with-learners', payload);
        onCreated && onCreated(data.session, data.enrolled_count);
      }
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[780px] rounded-2xl p-0 overflow-hidden">
        <div className="max-h-[88vh] overflow-y-auto">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#EFF0F6] bg-white sticky top-0 z-10">
            <DialogTitle className="text-[18px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {isEdit
                ? 'Modifier la session'
                : effectiveExistingSessionId
                  ? 'Pré-inscription à une session existante'
                  : preinscription
                    ? 'Nouvelle préinscription'
                    : 'Nouvelle session de formation'}
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              {isEdit
                ? 'Mettez à jour les informations de la session.'
                : effectiveExistingSessionId
                  ? `Les apprenants cochés seront ajoutés à la session existante ${effectiveExistingSessionId}. Aucune nouvelle session ne sera créée.`
                : preinscription
                ? 'Les apprenants du devis accepté sont pré-sélectionnés. Choisissez de créer une nouvelle session (en ajustant les dates) ou d\'inscrire à une session existante.'
                : 'Choisissez un programme pour pré-remplir la session, puis sélectionnez les apprenants.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            {/* Locked-from-quote banner */}
            {lockedFromQuote && !isEdit && (
              <div
                data-testid="session-locked-banner"
                className="rounded-xl border border-[#C7D2FE] bg-[#EEF2FF] px-4 py-3 text-[12.5px] text-[#3730A3] flex items-start gap-2"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold flex items-center flex-wrap gap-2">
                    <span>Champs verrouillés — issus du devis {lockedFromQuote.quoteNumber || 'signé'}</span>
                    {(preSelectedLearnerIds || []).length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="session-locked-learner-count"
                            title={`Apprenants prévus au devis : ${(preSelectedLearnerIds || []).length} — clic pour voir la liste`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-[#4F46E5] border border-[#C7B7FA] text-[11px] font-bold hover:bg-[#EEF2FF] transition-colors cursor-pointer"
                          >
                            <Users className="w-3 h-3" />
                            {(preSelectedLearnerIds || []).length} apprenant{(preSelectedLearnerIds || []).length > 1 ? 's' : ''} prévu{(preSelectedLearnerIds || []).length > 1 ? 's' : ''}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="bottom"
                          align="start"
                          className="w-72 p-3 rounded-xl border border-[#EFF0F6] bg-white shadow-lg"
                          data-testid="session-locked-learner-popover"
                        >
                          <div className="text-[11.5px] font-semibold uppercase tracking-wide text-[#6B6C85] mb-2">
                            Apprenants prévus au devis
                          </div>
                          <ul className="space-y-1.5 max-h-60 overflow-auto">
                            {(preSelectedLearnerIds || []).map((lid) => {
                              const l = (allLearners || []).find((x) => x.id === lid);
                              const name = l ? `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.email || lid : lid;
                              return (
                                <li key={lid} className="flex items-center gap-2 text-[12.5px] text-[#1A1B2E]">
                                  <UserPlus className="w-3 h-3 text-[#4F46E5] shrink-0" />
                                  <span className="truncate" title={name}>{name}</span>
                                  {l?.company && <span className="ml-auto text-[10.5px] text-[#8E8FA5] truncate">{l.company}</span>}
                                </li>
                              );
                            })}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <div className="opacity-80">Le programme, le tarif et la capacité sont repris du devis signé. Les <strong>dates de session sont éditables</strong> pour planifier la formation à la bonne période.</div>
                </div>
              </div>
            )}

            {/* Pré-inscription mode picker — only when locked from quote AND no
                session is already linked to the devis. Lets the user choose
                between creating a fresh session (with custom dates) or
                enrolling the learners into an existing session. */}
            {lockedFromQuote && !isEdit && !existingSessionId && (
              <div className="rounded-xl border border-[#EFF0F6] bg-white p-4 space-y-3" data-testid="session-pre-mode-picker">
                <h3 className="text-[13px] font-semibold text-[#1A1B2E] uppercase tracking-wide">Quelle session ?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${preMode === 'new' ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6] hover:border-[#D6D3FF]'}`}>
                    <input
                      type="radio"
                      name="pre_mode"
                      value="new"
                      checked={preMode === 'new'}
                      onChange={() => { setPreMode('new'); setChosenSessionId(''); }}
                      data-testid="session-pre-mode-new"
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-[#1A1B2E]">Créer une nouvelle session</div>
                      <div className="text-[11.5px] text-[#6B6C85] mt-0.5">Choisissez les dates de début/fin et inscrivez les apprenants du devis.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${preMode === 'existing' ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6] hover:border-[#D6D3FF]'}`}>
                    <input
                      type="radio"
                      name="pre_mode"
                      value="existing"
                      checked={preMode === 'existing'}
                      onChange={() => setPreMode('existing')}
                      data-testid="session-pre-mode-existing"
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-[#1A1B2E]">Inscrire à une session existante</div>
                      <div className="text-[11.5px] text-[#6B6C85] mt-0.5">Sélectionnez une session déjà planifiée pour le même programme.</div>
                    </div>
                  </label>
                </div>
                {preMode === 'existing' && (() => {
                  const matchingSessions = (allSessions || [])
                    .filter((s) => !values.programme_id || s.programme_id === values.programme_id || s.program === values.program)
                    .filter((s) => !['Terminée'].includes(s.status));
                  return (
                    <div className="space-y-2">
                      <label className="text-[12px] font-semibold text-[#4A4B63] block">Session existante</label>
                      <select
                        data-testid="session-pre-existing-select"
                        value={chosenSessionId}
                        onChange={(e) => setChosenSessionId(e.target.value)}
                        className="w-full h-10 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[13px] focus:border-[#4F46E5] outline-none"
                      >
                        <option value="">— Choisir une session —</option>
                        {matchingSessions.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.id} · {s.title || s.program} · {s.startDate || '—'} · {s.learners || 0}/{s.capacity || 0} · {s.status}
                          </option>
                        ))}
                      </select>
                      {!matchingSessions.length && (
                        <div className="text-[11.5px] text-[#B45309] bg-[#FEF3E7] border border-[#FCD9A8] rounded-lg px-3 py-2">
                          Aucune session active pour ce programme. Choisissez « Créer une nouvelle session » pour en planifier une.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* STEP 1 — Programme selector with category filter + autofill */}
            <section className="rounded-xl border border-[#C7D2FE] bg-[#FAFAFE] p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#4F46E5]" />
                  <h3 className="text-[13.5px] font-semibold text-[#1A1B2E]">{isEdit ? 'Programme & catégorie' : 'Auto-remplissage depuis un programme'}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Catégorie</label>
                    <select
                      data-testid="session-category-filter"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      disabled={!!lockedFromQuote}
                      className={`w-full h-10 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[13px] focus:border-[#4F46E5] outline-none ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                    >
                      {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Programme</label>
                    <select
                      data-testid="session-programme-select"
                      value={values.programme_id || ''}
                      onChange={(e) => handleProgrammeChange(e.target.value)}
                      disabled={!!lockedFromQuote}
                      className={`w-full h-10 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[13px] focus:border-[#4F46E5] outline-none ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                    >
                      <option value="">— Choisir un programme —</option>
                      {filteredPrograms.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {values.programme_id && (
                  <div className="text-[11.5px] text-[#0F766E] bg-[#E6FBF7] border border-[#A7F3D0] rounded-lg px-3 py-2 flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Informations auto-remplies.{values.nsf_code ? ` Spécialité NSF : ${nsfLabel(values.nsf_code)}.` : ''} Vous pouvez encore les modifier ci-dessous.</span>
                  </div>
                )}
              </section>

            {/* STEP 2 — Session fields */}
            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold text-[#1A1B2E] uppercase tracking-wide">Informations de la session</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <Field label="Intitulé" required full>
                  <input data-testid="session-field-title" required value={values.title} onChange={(e) => setVal('title', e.target.value)} disabled={!!lockedFromQuote} className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <Field label="Formateur" required>
                  <input data-testid="session-field-trainer" required value={values.trainer} onChange={(e) => setVal('trainer', e.target.value)} disabled={!!lockedFromQuote} className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <Field label="Mode" required>
                  <select value={values.mode} onChange={(e) => setVal('mode', e.target.value)} disabled={!!lockedFromQuote} className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}>
                    {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Date de début" required>
                  <input
                    data-testid="session-field-start-date"
                    type="date"
                    required
                    value={values.startDate}
                    onChange={(e) => setVal('startDate', e.target.value)}
                    disabled={!!effectiveExistingSessionId}
                    title={effectiveExistingSessionId ? `Date verrouillée — issue de la session existante ${effectiveExistingSessionId}.` : undefined}
                    className={`${inputCls} ${effectiveExistingSessionId ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                  />
                </Field>
                <Field label="Date de fin">
                  <input
                    data-testid="session-field-end-date"
                    type="date"
                    value={values.endDate}
                    onChange={(e) => setVal('endDate', e.target.value)}
                    disabled={!!effectiveExistingSessionId}
                    title={effectiveExistingSessionId ? `Date verrouillée — issue de la session existante ${effectiveExistingSessionId}.` : undefined}
                    className={`${inputCls} ${effectiveExistingSessionId ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                  />
                </Field>
                <Field label="Durée (heures)" required>
                  <input type="number" required value={values.duration} onChange={(e) => setVal('duration', Number(e.target.value))} disabled={!!lockedFromQuote} className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <div className="md:col-span-2">
                  <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Horaires</label>
                  <ChampHoraires
                    testId="session-field-horaires"
                    value={values.horaires}
                    onChange={(next) => setVal('horaires', next)}
                    reference={horaireType}
                    aide="Ils s’impriment sur le devis et sur la convention. Une formation découpée en séances affiche les horaires de ses séances : ce sont elles qui portent la journée qui commence à 14 h."
                  />
                </div>
                <Field label="Capacité" required>
                  <input
                    type="number"
                    required
                    value={values.capacity}
                    onChange={(e) => setVal('capacity', Number(e.target.value))}
                    disabled={!!lockedFromQuote}
                    title={lockedFromQuote ? 'Capacité verrouillée — issue du devis signé.' : undefined}
                    className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                  />
                </Field>
                <Field label="Tarif (€ HT)">
                  <input
                    type="number"
                    value={values.price}
                    onChange={(e) => setVal('price', Number(e.target.value))}
                    disabled={!!lockedFromQuote}
                    className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                    data-testid="session-field-price"
                  />
                </Field>
                <Field label="Statut">
                  <select
                    value={values.status}
                    onChange={(e) => setVal('status', e.target.value)}
                    disabled={!!lockedFromQuote}
                    title={lockedFromQuote ? 'Statut verrouillé en pré-inscription. Il évoluera automatiquement (Confirmée → Complète) selon le nombre d\'inscrits.' : undefined}
                    className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Lieu / Adresse" required full>
                  <div className="flex gap-2">
                    <input
                      data-testid="session-field-location"
                      required
                      value={values.location}
                      onChange={(e) => setVal('location', e.target.value)}
                      disabled={!!effectiveExistingSessionId}
                      title={effectiveExistingSessionId ? `Adresse verrouillée — issue de la session existante ${effectiveExistingSessionId}.` : undefined}
                      className={`${inputCls} ${effectiveExistingSessionId ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`}
                    />
                    {!effectiveExistingSessionId && lieux.length > 0 && (
                      <button
                        type="button"
                        data-testid="session-lieu-choisir"
                        onClick={() => setLieuOuvert((v) => !v)}
                        aria-expanded={lieuOuvert}
                        title="Choisir nos locaux ou l'adresse d'un client"
                        className="shrink-0 h-10 px-3 rounded-xl bg-[#EEF2FF] border border-[#C7D2FE] text-[12.5px] font-semibold text-[#3730A3] flex items-center gap-1.5 hover:bg-[#E0E7FF]"
                      >
                        <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                        {lieuOuvert ? 'Fermer' : 'Choisir'}
                      </button>
                    )}
                  </div>
                  {/* **La liste s'ouvre dans le flux, et c'est le troisieme
                      essai.** Un `Popover` de radix porte son contenu hors du
                      dialogue : invisible. Un bloc `absolute` se fait rogner
                      par le cadre qui defile : invisible aussi. Dans le flux,
                      il pousse le formulaire vers le bas — moins elegant, et
                      rien ne peut l'avaler. */}
                  {lieuOuvert && !effectiveExistingSessionId && (
                    <div
                      data-testid="session-lieu-liste"
                      className="mt-1.5 max-h-60 overflow-y-auto rounded-xl border border-[#C7D2FE] bg-white p-1.5"
                    >
                      {lieux.map((l) => (
                        <button
                          key={l.cle}
                          type="button"
                          disabled={l.sans_adresse}
                          onClick={() => { setVal('location', l.adresse); setLieuOuvert(false); }}
                          className={`w-full text-left px-2.5 py-2 rounded-lg ${l.sans_adresse ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#EEF2FF]'}`}
                        >
                          <span className="block text-[12.5px] font-semibold text-[#1A1B2E]">{l.libelle}</span>
                          <span className="block text-[11.5px] text-[#8E8FA5]">
                            {l.sans_adresse ? 'pas d’adresse sur sa fiche' : l.adresse}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
                <Field label="Description" full>
                  <textarea rows={2} value={values.description} onChange={(e) => setVal('description', e.target.value)} disabled={!!lockedFromQuote} className={`${textareaCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <Field label="Objectifs pédagogiques" full>
                  <textarea rows={2} value={values.objectives} onChange={(e) => setVal('objectives', e.target.value)} disabled={!!lockedFromQuote} className={`${textareaCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <Field label="Prérequis" full>
                  <textarea rows={2} value={values.prerequisites} onChange={(e) => setVal('prerequisites', e.target.value)} disabled={!!lockedFromQuote} className={`${textareaCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <Field label="Public cible" full>
                  <input value={values.public_cible} onChange={(e) => setVal('public_cible', e.target.value)} disabled={!!lockedFromQuote} className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
                <Field label="PDF du programme (URL)" full>
                  <input type="url" value={values.programme_pdf_url || ''} onChange={(e) => setVal('programme_pdf_url', e.target.value)} disabled={!!lockedFromQuote} placeholder="https://…" className={`${inputCls} ${lockedFromQuote ? 'opacity-60 cursor-not-allowed bg-[#F6F7FB]' : ''}`} />
                </Field>
              </div>

              {/* Vague 98.9 — Multi-date ranges (extracted to SessionDateRangesEditor) */}
              {!effectiveExistingSessionId && (
                <SessionDateRangesEditor
                  dateRanges={values.date_ranges}
                  onChange={(next) => setVal('date_ranges', next)}
                />
              )}
            </section>

            {/* STEP 3 — Learners selection (only on create; edit mode uses enrollment sheet) */}
            {!isEdit && (
              <section className="space-y-3">
                {existingSessionId && visibleExistingEnrollments.length > 0 && (
                  <div data-testid="session-existing-enrollments" className="rounded-xl border border-[#FFE7C2] bg-[#FFF8EC] px-4 py-3">
                    <div className="text-[11.5px] font-semibold uppercase tracking-wide text-[#A06617] mb-2">
                      Déjà inscrits sur cette session{companyScope?.name ? ` · ${companyScope.name}` : ''} ({visibleExistingEnrollments.length})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {visibleExistingEnrollments.map((l) => (
                        <span key={l.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-lg bg-white text-[11.5px] text-[#1A1B2E] border border-[#EFF0F6]">
                          <span className="w-5 h-5 rounded-full brand-gradient text-white text-[9px] font-semibold flex items-center justify-center">
                            {l.firstName?.[0]}{l.lastName?.[0]}
                          </span>
                          {l.firstName} {l.lastName}
                          <button
                            type="button"
                            data-testid={`session-unenroll-${l.id}`}
                            onClick={async () => {
                              if (!confirm(`Désinscrire ${l.firstName} ${l.lastName} de cette session ?`)) return;
                              try {
                                await api.delete(`/sessions/${existingSessionId}/enroll/${l.id}`);
                                setExistingEnrollments((prev) => prev.filter((x) => x.id !== l.id));
                                toast({ title: 'Apprenant désinscrit', description: `${l.firstName} ${l.lastName}` });
                              } catch (err) {
                                toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Désinscription impossible' });
                              }
                            }}
                            title="Désinscrire de la session"
                            className="ml-1 w-5 h-5 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="text-[11px] text-[#A06617] mt-2">
                      Seuls les apprenants <strong>supplémentaires</strong> cochés ci-dessous seront ajoutés.
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <h3 className="text-[13px] font-semibold text-[#1A1B2E] uppercase tracking-wide">
                    {existingSessionId ? 'Nouveaux apprenants à ajouter' : 'Apprenants à inscrire'}{companyScope?.name ? ` · ${companyScope.name}` : ''} <span className="text-[#4F46E5]">({selectedLearners.size})</span>
                  </h3>
                  <button
                    type="button"
                    data-testid="session-inline-learner-toggle"
                    onClick={() => setInlineOpen((v) => !v)}
                    className="h-9 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF]"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> {inlineOpen ? 'Fermer' : 'Créer un apprenant'}
                  </button>
                </div>

                {inlineOpen && (
                  <div className="rounded-xl border border-[#C7D2FE] bg-[#EEF2FF]/40 p-3 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <input data-testid="inline-learner-firstname" placeholder="Prénom *" value={newLearner.firstName} onChange={(e) => setNewLearner({ ...newLearner, firstName: e.target.value })} className={inputCls} />
                      <input data-testid="inline-learner-lastname" placeholder="Nom *" value={newLearner.lastName} onChange={(e) => setNewLearner({ ...newLearner, lastName: e.target.value })} className={inputCls} />
                      <input data-testid="inline-learner-email" type="email" placeholder="Email *" value={newLearner.email} onChange={(e) => setNewLearner({ ...newLearner, email: e.target.value })} className={inputCls + ' sm:col-span-2'} />
                      <input data-testid="inline-learner-phone" placeholder="Téléphone" value={newLearner.phone} onChange={(e) => setNewLearner({ ...newLearner, phone: e.target.value })} className={inputCls} />
                      <input data-testid="inline-learner-company" placeholder="Entreprise" value={newLearner.company} onChange={(e) => setNewLearner({ ...newLearner, company: e.target.value })} className={inputCls} />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => { setInlineOpen(false); setNewLearner(EMPTY_LEARNER); }} className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63]">Annuler</button>
                      <button
                        type="button"
                        data-testid="inline-learner-save"
                        onClick={handleCreateInlineLearner}
                        disabled={creatingLearner}
                        className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-60"
                      >
                        {creatingLearner && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Créer & sélectionner
                      </button>
                    </div>
                  </div>
                )}

                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
                  <input
                    placeholder="Rechercher un apprenant…"
                    value={learnerSearch}
                    onChange={(e) => setLearnerSearch(e.target.value)}
                    className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-[#EFF0F6] text-[13px] focus:border-[#4F46E5] outline-none"
                  />
                </div>

                <div className="rounded-xl border border-[#EFF0F6] max-h-[220px] overflow-y-auto">
                  {filteredLearners.length === 0 && (
                    <div className="text-[12px] italic text-[#8E8FA5] text-center py-6">Aucun apprenant ne correspond.</div>
                  )}
                  {filteredLearners.map((l) => {
                    const checked = selectedLearners.has(l.id);
                    return (
                      <button
                        type="button"
                        key={l.id}
                        data-testid={`learner-pick-${l.id}`}
                        onClick={() => toggleLearner(l.id)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2 border-b border-[#F3F4F8] last:border-0 hover:bg-[#FAFAFE] ${checked ? 'bg-[#EEF2FF]' : ''}`}
                      >
                        {checked ? <CheckCircle2 className="w-4 h-4 text-[#4F46E5]" /> : <Circle className="w-4 h-4 text-[#D4D5E0]" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{l.firstName} {l.lastName}</div>
                          <div className="text-[11px] text-[#8E8FA5] truncate">{l.email}{l.company ? ` · ${l.company}` : ''}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {error && (
              <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
            )}

            <DialogFooter className="pt-2">
              <button type="button" onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">Annuler</button>
              <button type="submit" data-testid="session-submit-btn" disabled={loading} className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEdit
                  ? 'Enregistrer'
                  : existingSessionId
                    ? 'Finaliser la pré-inscription'
                    : 'Créer la session'}
              </button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
